<template>
  <teleport to="body">
    <transition
      name="modal-fade"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="visible"
        class="makercart-modal-overlay"
        @click.self="closeModal"
      >
        <div class="makercart-modal-container">
          <!-- Loading State -->
          <div v-if="isLoadingDetail" class="modal-loading-overlay">
            <div class="modal-loading-content">
              <div class="modal-loading-spinner"></div>
              <div class="modal-loading-text">Loading kit details...</div>
              <div class="modal-loading-subtitle">
                Please wait while we fetch the information
              </div>
            </div>
          </div>

          <!-- Main Modal Content -->
          <div v-else class="modal-main-content makercrate-modal-main-content">
            <button
              type="button"
              class="makercart-modal-close"
              aria-label="Close kit details"
              @click="closeModal"
            >
              <CloseCircleIcon color="#3B4648" />
            </button>

            <div class="makercart-modal-content-container">
              <!-- ===== MOBILE STICKY HEADER ===== -->
              <div v-if="isMobile" class="mc-mobile-sticky-header">
                <button
                  type="button"
                  class="mobile-makercart-modal-close"
                  aria-label="Close kit details"
                  @click="closeModal"
                >
                  <CloseCircleIcon width="24" height="24" color="#3B4648" />
                </button>
                <div class="mc-mobile-header-info">
                  <MakeyMakeyBadge
                    v-if="makerCartData.hasMakeyBadge"
                    :container-style="{ marginBottom: '8px' }"
                  />
                  <div class="mc-mobile-title-block">
                    <h1 class="mc-mobile-title">{{ makerCartData.title }}</h1>
                    <span class="mc-mobile-subtitle">{{
                      makerCartData.subtitle
                    }}</span>
                  </div>
                  <p v-if="makerCartData.price" class="mc-mobile-price">
                    ${{ makerCartData.price }}
                  </p>
                  <div
                    v-if="hasKitMetaBadges"
                    class="kit-meta-badges kit-meta-badges--mobile"
                  >
                    <span
                      v-if="kitPurchaseInfo.studentsPerKit"
                      class="kit-meta-badge"
                    >
                      Students per kit:
                      <strong>{{ kitPurchaseInfo.studentsPerKit }}</strong>
                    </span>
                    <span
                      v-if="kitPurchaseInfo.instructionalHours"
                      class="kit-meta-badge"
                    >
                      Instructional hours:
                      <strong>{{ kitPurchaseInfo.instructionalHours }}</strong>
                    </span>
                  </div>
                </div>
              </div>

              <!-- ===== DESKTOP HEADER ===== -->
              <div v-if="!isMobile" class="makercart-content-header">
                <MakeyMakeyBadge v-if="makerCartData.hasMakeyBadge" />

                <div class="makercart-modal-title-container mc-product-info">
                  <div class="mc-product-titles">
                    <h1 class="makercart-modal-title">
                      {{ makerCartData.title }}
                    </h1>
                    <span class="makercart-modal-subtitle">{{
                      makerCartData.subtitle
                    }}</span>
                  </div>
                  <p v-if="makerCartData.price" class="makercart-modal-price">
                    ${{ makerCartData.price }}
                  </p>
                </div>

                <div v-if="hasKitMetaBadges" class="kit-meta-badges">
                  <span
                    v-if="kitPurchaseInfo.studentsPerKit"
                    class="kit-meta-badge"
                  >
                    Students per kit:
                    <strong>{{ kitPurchaseInfo.studentsPerKit }}</strong>
                  </span>
                  <span
                    v-if="kitPurchaseInfo.instructionalHours"
                    class="kit-meta-badge"
                  >
                    Instructional hours:
                    <strong>{{ kitPurchaseInfo.instructionalHours }}</strong>
                  </span>
                </div>

                <div class="mc-hero-gallery">
                  <button
                    type="button"
                    class="mc-hero-nav-btn"
                    aria-label="Show previous images"
                    :disabled="!canHeroPrev"
                    @click="prevHero"
                  >
                    <ChevronLeftIcon color="var(--primary-blue)" />
                  </button>
                  <div class="mc-hero-images">
                    <button
                      v-for="(image, index) in visibleHeroImages"
                      :key="`${image}-${heroStartIndex + index}`"
                      type="button"
                      class="mc-hero-image-wrap"
                      @click="openKitImage(heroStartIndex + index)"
                    >
                      <img
                        :src="image"
                        :alt="`${makerCartData.title} - Image ${
                          heroStartIndex + index + 1
                        }`"
                        class="mc-hero-image"
                        @error="handleImageError"
                      />
                      <img
                        src="/images/icon/expand-image.svg"
                        alt=""
                        class="mc-hero-expand"
                        width="24"
                        height="24"
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    class="mc-hero-nav-btn"
                    aria-label="Show next images"
                    :disabled="!canHeroNext"
                    @click="nextHero"
                  >
                    <ChevronRightIcon color="var(--primary-blue)" />
                  </button>
                </div>

                <p class="main-description">
                  {{ makerCartData.description }}
                </p>

                <button
                  v-if="kitPurchaseInfo?.materialListUrl"
                  type="button"
                  class="sample-materials-btn kit-material-download-btn"
                  @click="openMaterialList"
                >
                  Download material list
                </button>

                <div class="main-description-divider"></div>

                <div
                  class="activity-tabs-container"
                  v-if="
                    makerCartData.activities &&
                    makerCartData.activities.length > 0
                  "
                >
                  <div class="activity-tabs">
                    <button
                      type="button"
                      v-for="(activity, index) in makerCartData.activities"
                      :key="index"
                      :class="[
                        'activity-tab',
                        { active: activeActivityIndex === index },
                      ]"
                      @click="onActivityTabClick(index)"
                    >
                      {{ activity.title }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="makercart-modal-content-content">
                <div
                  v-if="!isMobile && displayImages.length"
                  class="mc-activity-thumbs"
                >
                  <button
                    v-for="(image, index) in displayImages"
                    :key="index"
                    type="button"
                    class="mc-activity-thumb"
                    @click="openImageGalleryWithIndex(index)"
                  >
                    <img
                      :src="image"
                      :alt="`${makerCartData.title} - Image ${index + 1}`"
                      class="mc-activity-thumb-image"
                      @error="handleImageError"
                    />
                  </button>
                  <button
                    v-if="hasMoreImages"
                    type="button"
                    class="mc-view-more-btn"
                    @click="showImageGallery"
                  >
                    <span class="mc-view-more-text">View<br />More</span>
                    <ArrowShortRightIcon
                      width="24"
                      height="24"
                      color="#00925e"
                    />
                  </button>
                </div>

                <div v-if="isMobile" class="makercart-gallery-section">
                  <div class="main-image-container">
                    <img
                      :src="currentImage"
                      :alt="makerCartData.title"
                      class="main-image"
                      @error="handleImageError"
                      @click="openImageGalleryWithIndex(currentImageIndex)"
                    />
                    <div
                      v-if="allImages.length > 1"
                      class="mc-mobile-image-nav"
                    >
                      <button
                        type="button"
                        class="mc-mobile-nav-btn"
                        aria-label="Show previous image"
                        @click="previousImage"
                        :disabled="currentImageIndex === 0"
                      >
                        <ChevronLeftIcon
                          width="16"
                          height="16"
                          color="var(--primary-blue)"
                        />
                      </button>
                      <button
                        type="button"
                        class="mc-mobile-nav-btn"
                        aria-label="Show next image"
                        @click="nextImage"
                        :disabled="currentImageIndex === allImages.length - 1"
                      >
                        <ChevronRightIcon
                          width="16"
                          height="16"
                          color="var(--primary-blue)"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Mobile: Description section (#ededfc bg) -->
                <div v-if="isMobile" class="mc-mobile-description-section">
                  <p class="mc-mobile-description">
                    {{ makerCartData.description }}
                  </p>
                </div>

                <div
                  v-if="isMobile && kitPurchaseInfo?.materialListUrl"
                  class="kit-material-download-section"
                >
                  <button
                    type="button"
                    class="sample-materials-btn kit-material-download-btn kit-material-download-btn--mobile"
                    @click="openMaterialList"
                  >
                    Download material list
                  </button>
                </div>

                <!-- Mobile: Activity selector (horizontal pill tabs) -->
                <div
                  v-if="
                    isMobile &&
                    makerCartData.activities &&
                    makerCartData.activities.length > 0
                  "
                  class="mc-mobile-activity-bar"
                >
                  <div class="mc-mobile-activity-tabs">
                    <button
                      type="button"
                      v-for="(activity, index) in makerCartData.activities"
                      :key="index"
                      :class="[
                        'mc-mobile-activity-tab',
                        { active: activeActivityIndex === index },
                      ]"
                      @click="onActivityTabClick(index)"
                    >
                      {{ activity.title }}
                    </button>
                  </div>
                </div>

                <!-- Center Section: Content -->
                <div class="makercart-content-section" v-if="currentActivity">
                  <!-- Current Activity Title + Tags -->
                  <div
                    :class="
                      isMobile ? 'mc-mobile-section' : 'mc-activity-heading'
                    "
                  >
                    <h2 class="current-activity-title">
                      {{ currentActivity.title }}
                    </h2>

                    <div class="tags-container">
                      <button
                        type="button"
                        v-for="(tag, idx) in currentActivity.tags"
                        :key="`${tag}-${idx}`"
                        class="tag-button"
                      >
                        {{ tag }}
                      </button>
                    </div>
                  </div>

                  <!-- Divider (desktop) -->
                  <div v-if="!isMobile" class="divider"></div>

                  <!-- Overview + Lesson Outcomes -->
                  <template v-if="isMobile">
                    <div class="mc-mobile-section">
                      <h3 class="section-title">Overview</h3>
                      <p class="section-content">
                        {{ currentActivity.overview }}
                      </p>
                    </div>
                    <div class="mc-mobile-section">
                      <h3 class="section-title">Lesson Outcomes</h3>
                      <div class="section-content">
                        <div
                          class="formatted-outcomes"
                          v-html="formatOutcomes(currentActivity.outcomes)"
                        ></div>
                      </div>
                    </div>
                  </template>
                  <div v-else class="content-sections">
                    <div class="content-column">
                      <h3 class="section-title">Overview</h3>
                      <p class="section-content">
                        {{ currentActivity.overview }}
                      </p>
                    </div>
                    <div class="content-column">
                      <h3 class="section-title">Lesson Outcomes</h3>
                      <div class="section-content">
                        <div
                          class="formatted-outcomes"
                          v-html="formatOutcomes(currentActivity.outcomes)"
                        ></div>
                      </div>
                    </div>
                  </div>

                  <!-- Lesson Materials Button -->
                  <div
                    :class="
                      isMobile
                        ? 'mc-mobile-section mc-mobile-section-center'
                        : ''
                    "
                  >
                    <CustomTooltip
                      placement="top"
                      :text="currentActivity?.note || ''"
                      v-if="
                        (currentActivity?.is_disabled ||
                          !currentActivity?.public_visibility) &&
                        currentActivity?.note
                      "
                    >
                      <template #trigger>
                        <button
                          type="button"
                          class="sample-materials-btn"
                          :disabled="
                            currentActivity?.is_disabled ||
                            !currentActivity?.public_visibility
                          "
                          @click="viewLessonMaterials"
                        >
                          <ExternalLinkIcon width="20" height="20" />
                          Lesson Materials
                        </button>
                      </template>
                    </CustomTooltip>
                    <button
                      v-else
                      type="button"
                      class="sample-materials-btn"
                      :disabled="
                        currentActivity?.is_disabled ||
                        !currentActivity?.public_visibility
                      "
                      @click="viewLessonMaterials"
                    >
                      <ExternalLinkIcon width="20" height="20" />
                      Lesson Materials
                    </button>
                  </div>

                  <div
                    :class="
                      isMobile ? 'mc-mobile-section mc-mobile-section-nopd' : ''
                    "
                  >
                    <div class="mc-details">
                      <button
                        type="button"
                        class="mc-details-toggle"
                        :class="{ expanded: showDetails }"
                        @click="toggleDetails"
                      >
                        <span>View Details</span>
                        <ChevronDownIcon width="24" height="24" />
                      </button>

                      <transition name="details-expand">
                        <div v-if="showDetails" class="mc-details-panel">
                          <div class="details-grid">
                            <div class="detail-column">
                              <div class="detail-title-with-icon">
                                <CustomTooltip
                                  placement="top"
                                  text="Each cart is packed with enough materials to run your chosen activities—scaled to your selected student count."
                                >
                                  <template #trigger>
                                    <img
                                      src="/images/icon/i-circle-icon.svg"
                                      class="mc-info-icon"
                                      width="24"
                                      height="24"
                                      alt=""
                                    />
                                  </template>
                                </CustomTooltip>
                                <h4 class="detail-title">Materials provided</h4>
                              </div>
                              <ol class="detail-list">
                                <li
                                  v-if="loadingMaterials"
                                  class="loading-materials"
                                >
                                  Loading materials...
                                </li>
                                <li
                                  v-else
                                  v-for="material in materialsProvided"
                                  :key="material"
                                >
                                  {{ material }}
                                </li>
                                <li
                                  v-if="
                                    !loadingMaterials &&
                                    materialsProvided.length === 0
                                  "
                                >
                                  No materials provided for this activity
                                </li>
                              </ol>
                            </div>

                            <div class="detail-column border-column">
                              <div class="detail-title-with-icon">
                                <CustomTooltip
                                  placement="top"
                                  text="These items are not provided as part of the Makercart order."
                                >
                                  <template #trigger>
                                    <img
                                      src="/images/icon/i-circle-icon.svg"
                                      class="mc-info-icon"
                                      width="24"
                                      height="24"
                                      alt=""
                                    />
                                  </template>
                                </CustomTooltip>
                                <h4 class="detail-title">
                                  You'll need to provide
                                </h4>
                              </div>
                              <ol class="detail-list">
                                <li
                                  v-if="loadingMaterials"
                                  class="loading-materials"
                                >
                                  Loading materials...
                                </li>
                                <li
                                  v-else
                                  v-for="material in materialsToProvide"
                                  :key="material"
                                >
                                  {{ material }}
                                </li>
                                <li
                                  v-if="
                                    !loadingMaterials &&
                                    materialsToProvide.length === 0
                                  "
                                >
                                  No additional materials needed
                                </li>
                              </ol>
                            </div>

                            <div class="detail-column border-column">
                              <h4 class="detail-title">Required Equipment</h4>
                              <ol class="detail-list">
                                <li
                                  v-for="equipment in currentActivity.requiredEquipment"
                                  :key="equipment"
                                >
                                  {{ equipment }}
                                </li>
                                <li
                                  v-if="
                                    (currentActivity.requiredEquipment || [])
                                      .length === 0
                                  "
                                >
                                  No Hardware
                                </li>
                              </ol>
                            </div>
                          </div>
                          <div class="mc-standards">
                            <h4 class="detail-title">Standards</h4>
                            <ol class="detail-list">
                              <li>
                                {{ currentActivity.standards || "N/A" }}
                              </li>
                            </ol>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Section: Sidebar (desktop only) -->
            <div class="makercart-sidebar" v-if="!isMobile">
              <div class="sidebar-content makercrate-sidebar-content">
                <div class="sidebar-actions makercrate-sidebar-actions">
                  <ActionButton
                    variant="primary"
                    full-width
                    :loading="isAddingToCart"
                    @click="addToMakercart"
                  >
                    Add to Cart
                    <template #icon-right>
                      <ShoppingCartIcon width="24" height="24" color="#ffff" />
                    </template>
                  </ActionButton>
                  <button
                    type="button"
                    class="makercrate-sidebar-share-btn share-btn"
                  >
                    <ShareIcon width="24" height="24" color="#003d6a" />
                    Share
                  </button>
                </div>
              </div>
            </div>

            <!-- Mobile Footer (Sticky) -->
            <div v-if="isMobile" class="mc-mobile-footer">
              <ActionButton
                variant="primary"
                full-width
                :loading="isAddingToCart"
                @click="addToMakercart"
              >
                Add to Cart
                <template #icon-right>
                  <ShoppingCartIcon width="24" height="24" color="#ffff" />
                </template>
              </ActionButton>
              <button type="button" class="mc-footer-btn mc-footer-btn-ghost">
                <ShareIcon width="20" height="20" color="#003d6a" />
                Share
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Image Gallery Modal -->
    <ImageGalleryModal
      ref="galleryModalRef"
      :visible="showImageGalleryModal"
      :images="galleryImageList"
      :initial-index="galleryInitialIndex"
      @close="closeImageGallery"
    />
  </teleport>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from "vue";
import CustomTooltip from "@/components/common/CustomTooltip.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import MakeyMakeyBadge from "./MakeyMakeyBadge.vue";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  ShareIcon,
  CloseCircleIcon,
  ShoppingCartIcon,
} from "@/components/icons";
import ImageGalleryModal from "@/components/maker-cart/ImageGalleryModal.vue";
import { useCart } from "@/composeables/useCart";
import makercrateService from "@/services/makercrateService";
import stemStudioService from "@/services/stemStudioService";
import ArrowShortRightIcon from "@/components/icons/ArrowShortRightIcon.vue";
import { useLocaleStore } from "@/store/locale";
import { getApiError } from "@/utils/apiError";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  makercartId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["close"]);

const notify = inject("notify");
const localeStore = useLocaleStore();
const { syncCartWithAPI } = useCart();

const isMobile = ref(window.innerWidth <= 768);
const activeActivityIndex = ref(0);
const currentImageIndex = ref(0);
const showDetails = ref(false);
const isLoadingDetail = ref(false);
const loadingMaterials = ref(false);
const isAddingToCart = ref(false);

// Image Gallery Modal State
const showImageGalleryModal = ref(false);
const galleryModalRef = ref(null);
const galleryInitialIndex = ref(0);
const galleryImageList = ref([]);
const materialsProvided = ref([]);
const materialsToProvide = ref([]);
const heroStartIndex = ref(0);
const HERO_PAGE_SIZE = 3;

const checkScreenSize = () => {
  isMobile.value = window.innerWidth <= 768;
};

const handleEscKey = (event) => {
  if (event.key !== "Escape" || !props.visible) return;
  if (showImageGalleryModal.value) return;
  closeModal();
};

onMounted(() => {
  window.addEventListener("resize", checkScreenSize);
  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkScreenSize);
  document.removeEventListener("keydown", handleEscKey);
  document.body.style.overflow = "";
  document.documentElement.style.overflow = "";
});

const closeModal = () => {
  emit("close");
};

const onEnter = (el) => {
  el.style.opacity = "0";
  el.style.transform = "scale(0.9)";
};
const onAfterEnter = (el) => {
  el.style.opacity = "1";
  el.style.transform = "scale(1)";
};
const onLeave = (el) => {
  el.style.opacity = "0";
  el.style.transform = "scale(0.9)";
};
const onAfterLeave = () => {
  document.body.style.overflow = "";
  document.documentElement.style.overflow = "";
};

const toggleDetails = () => {
  showDetails.value = !showDetails.value;
};

const viewLessonMaterials = () => {
  if (currentActivity.value?.makercart_id) {
    // Open lesson materials in new tab
    window.open(
      `/lesson-materials/${currentActivity.value.makercart_id}?locale=${localeStore.currentLocale}&source=makercrate`,
      "_blank"
    );
  }
};

const openMaterialList = () => {
  const url = makerCartData.value.materialListUrl;
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

// Default empty data shape
const defaultData = {
  id: null,
  title: "",
  subtitle: "",
  price: "",
  description: "",
  hasMakeyBadge: false,
  studentsPerKit: "",
  instructionalHours: "",
  materialListUrl: "",
  activities: [],
  images: [],
};

const makerCartData = ref({ ...defaultData });

// ---------- Data mapping ----------
const mapApiToModalData = (data) => {
  const activities = (data.makercrate_activities || []).map((act) => {
    const tags = [
      ...(act.grade_level || []),
      act.subject,
      act.duration,
      ...(act.hardware_associated || []).filter(
        (h) => h !== "No Equipment Required"
      ),
      ...(Array.isArray(act.tags) ? act.tags : []),
    ].filter((tag, index, arr) => tag && arr.indexOf(tag) === index);

    return {
      title: act.makercart_activity,
      tags,
      overview: act.activity_description,
      outcomes: act.learning_outcomes || "",
      materialIds: act.material_needed || [],
      requiredEquipment: (act.hardware_associated || []).filter(
        (h) => h !== "No Equipment Required"
      ),
      standards: act.standards || "",
      images: act.image_thumbnail || [],
      makercart_id: act.makercart_id,
      public_visibility: act.public_visibility,
      is_disabled: act.is_disabled,
      note: act.note,
    };
  });

  return {
    id: data.id || null,
    title: data.title || "",
    subtitle: data.subheader || "",
    price: data.price || "",
    description: data.description || "",
    hasMakeyBadge: !!data.hardware,
    studentsPerKit: data.students_per_kit || "",
    instructionalHours: data.instructional_hours || "",
    materialListUrl: data.material_list_url || "",
    images: (data.images || [])
      .map(
        (image) =>
          image.url ||
          image.thumbnails?.full?.url ||
          image.thumbnails?.large?.url
      )
      .filter(Boolean),
    activities,
  };
};

// ---------- Fetch detail ----------
const fetchMakercrateDetail = async (id) => {
  if (!id) return;
  isLoadingDetail.value = true;
  materialsProvided.value = [];
  materialsToProvide.value = [];
  try {
    const response = await makercrateService.getMakercrateById(id);
    if (response?.data) {
      makerCartData.value = mapApiToModalData(response.data);
      activeActivityIndex.value = 0;
      currentImageIndex.value = 0;
      heroStartIndex.value = 0;
      galleryImageList.value = [];
      showDetails.value = false;
      // Fetch materials for the first activity
      if (makerCartData.value.activities.length > 0) {
        await fetchActivityMaterials(makerCartData.value.activities[0]);
      }
    }
  } catch (error) {
    console.error("Failed to fetch makercrate detail:", error);
  } finally {
    isLoadingDetail.value = false;
  }
};

// ---------- Materials ----------
const fetchActivityMaterials = async (activity) => {
  if (!activity?.materialIds?.length) {
    materialsProvided.value = [];
    materialsToProvide.value = [];
    return;
  }
  try {
    loadingMaterials.value = true;
    const ids = activity.materialIds.join(",");
    const response = await stemStudioService.getMaterialByIdBulk(ids);
    materialsProvided.value = [];
    materialsToProvide.value = [];
    if (response?.status === 200 && response.data?.data) {
      response.data.data.forEach((item) => {
        if (item.includedInMC) {
          materialsProvided.value.push(item.name);
        } else {
          materialsToProvide.value.push(item.name);
        }
      });
    }
  } catch (error) {
    console.error("Error fetching activity materials:", error);
    materialsProvided.value = [];
    materialsToProvide.value = [];
  } finally {
    loadingMaterials.value = false;
  }
};

// ---------- Activity tab ----------
const onActivityTabClick = async (index) => {
  activeActivityIndex.value = index;
  currentImageIndex.value = 0;
  materialsProvided.value = [];
  materialsToProvide.value = [];
  if (makerCartData.value.activities[index]) {
    await fetchActivityMaterials(makerCartData.value.activities[index]);
  }
};

// ---------- Computed ----------
const kitPurchaseInfo = computed(() => {
  const { studentsPerKit, instructionalHours, materialListUrl } =
    makerCartData.value;
  if (!studentsPerKit && !instructionalHours && !materialListUrl) {
    return null;
  }
  return { studentsPerKit, instructionalHours, materialListUrl };
});

const hasKitMetaBadges = computed(() => {
  return !!(
    kitPurchaseInfo.value?.studentsPerKit ||
    kitPurchaseInfo.value?.instructionalHours
  );
});

const currentActivity = computed(() => {
  const acts = makerCartData.value.activities;
  if (!acts || acts.length === 0) return null;
  return acts[activeActivityIndex.value] || acts[0];
});

const allImages = computed(() => currentActivity.value?.images || []);
const kitImages = computed(() => makerCartData.value.images || []);

const visibleHeroImages = computed(() => {
  const images = kitImages.value;
  if (!images.length) return ["/images/content-placeholder.svg"];
  return images.slice(
    heroStartIndex.value,
    heroStartIndex.value + HERO_PAGE_SIZE
  );
});

const canHeroPrev = computed(() => heroStartIndex.value > 0);

const canHeroNext = computed(() => {
  return heroStartIndex.value + HERO_PAGE_SIZE < kitImages.value.length;
});

const prevHero = () => {
  if (canHeroPrev.value) heroStartIndex.value -= 1;
};

const nextHero = () => {
  if (canHeroNext.value) heroStartIndex.value += 1;
};

const displayImages = computed(() => allImages.value.slice(0, 5));

const hasMoreImages = computed(() => allImages.value.length > 5);

const currentImage = computed(() => {
  if (allImages.value.length > 0) {
    return allImages.value[currentImageIndex.value] || allImages.value[0];
  }
  return "/images/content-placeholder.svg";
});

const previousImage = () => {
  if (currentImageIndex.value > 0) currentImageIndex.value--;
};

const nextImage = () => {
  if (currentImageIndex.value < allImages.value.length - 1)
    currentImageIndex.value++;
};

const showImageGallery = () => {
  galleryImageList.value = allImages.value;
  galleryInitialIndex.value = 0;
  showImageGalleryModal.value = true;
};

const openImageGalleryWithIndex = (index) => {
  galleryImageList.value = allImages.value;
  galleryInitialIndex.value = index || 0;
  showImageGalleryModal.value = true;
};

const openKitImage = (index) => {
  galleryImageList.value = kitImages.value.length
    ? kitImages.value
    : ["/images/content-placeholder.svg"];
  galleryInitialIndex.value = index || 0;
  showImageGalleryModal.value = true;
};

const closeImageGallery = () => {
  showImageGalleryModal.value = false;
};

const handleImageError = (event) => {
  event.target.src = "/images/content-placeholder.svg";
};

const formatOutcomes = (text) => {
  if (!text) return "";
  let formatted = text.replace(
    /Students will\.\.\./g,
    "<strong>Students will...</strong>"
  );
  return formatted.replace(/\n/g, "<br/>");
};

// ---------- Watches ----------
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      fetchMakercrateDetail(props.makercartId);
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      makerCartData.value = { ...defaultData };
    }
  },
  { immediate: true }
);

watch(
  () => props.makercartId,
  (newId) => {
    if (props.visible && newId) {
      fetchMakercrateDetail(newId);
    }
  }
);

const addToMakercart = async () => {
  const makercrateId = makerCartData.value.id || props.makercartId;
  if (!makercrateId || isAddingToCart.value) return;

  isAddingToCart.value = true;
  try {
    const response = await makercrateService.addToCart(
      makercrateId,
      localeStore.currentLocale
    );
    if (response?.success === false) {
      notify?.error(response.message || "Failed to add makercrate to cart");
      return;
    }

    // Refresh shared cart state so "View your cart" badge updates
    await syncCartWithAPI();

    notify?.success("Successfully added to cart!");
    emit("close");
  } catch (error) {
    console.error("Failed to add makercrate to cart:", error);
    const { message } = getApiError(error, "Failed to add makercrate to cart");
    notify?.error(message);
  } finally {
    isAddingToCart.value = false;
  }
};
</script>

<style scoped src="@/assets/css/catalog-makercart.css"></style>
<style scoped>
.makercart-modal-container {
  width: 100vw !important;
  max-width: 100vw !important;
  height: 100vh !important;
  max-height: 100vh !important;
  border-radius: 0 !important;
  margin: 0 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  bottom: 0 !important;
  right: 0 !important;
  box-shadow: none !important;
  overflow-x: hidden !important;
}

.modal-loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 300px;
}

.modal-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.modal-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #d8dae5;
  border-top-color: var(--primary-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-loading-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 600;
  font-size: var(--fs-medium);
  color: var(--primary-blue);
}

.modal-loading-subtitle {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  color: #6c737f;
}

/* Makercrate-only: match Figma Product Info subtitle (Headline Medium/Regular) */
.makercart-modal-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: var(--primary-blue);
  margin: 0;
}

.makercart-modal-title {
  margin: 0;
  padding-right: 0;
}

.loading-materials {
  color: #6c737f;
  font-style: italic;
}

.kit-meta-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
}

.kit-meta-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  height: 24px;
  padding: 0 16px;
  border-radius: 999px;
  background-color: var(--purple-light-2);
  color: var(--purple-dark-2);
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-xs);
  line-height: 16px;
  letter-spacing: 0.048px;
}

.kit-meta-badge strong {
  font-weight: 700;
}

.kit-meta-badges--mobile {
  margin: 4px 0 0;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
}

.kit-meta-badges--mobile .kit-meta-badge {
  white-space: nowrap;
}

.kit-material-download-btn {
  margin: 0 !important;
  align-self: flex-start;
  width: 233px;
  height: 40px;
  justify-content: center;
}

.kit-material-download-section {
  width: 100%;
  padding: 16px;
  background: white;
  flex-shrink: 0;
}

.kit-material-download-btn--mobile {
  width: 100%;
  justify-content: center;
  margin: 0 !important;
}

.makercrate-modal-main-content {
  grid-template-columns: minmax(0, 1fr) 301.5px;
  align-items: start;
  gap: 32px;
  padding: 56px 24px 24px;
}

.makercrate-modal-main-content .makercart-modal-close {
  top: 16px;
  right: 16px;
  z-index: 3;
}

.makercrate-modal-main-content .makercart-content-header {
  margin-top: 0;
  padding: 0 32px;
  gap: 24px;
  min-width: 0;
  max-width: 100%;
}

.makercrate-modal-main-content .main-description-divider {
  margin: 0;
}

.makercrate-modal-main-content .makercart-modal-content-container {
  gap: 24px;
  min-width: 0;
}

.makercrate-modal-main-content .makercart-modal-content-content {
  padding: 0 32px 32px;
  gap: 32px;
  flex-wrap: nowrap;
  align-items: flex-start;
}

.makercrate-modal-main-content .makercart-content-section {
  gap: 24px;
  padding-right: 0;
}

.makercrate-modal-main-content .makercart-sidebar {
  width: 301.5px;
  margin-top: 0;
  margin-right: 0;
  max-height: none;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
}

.mc-product-info {
  align-items: flex-end;
  gap: 16px;
}

.mc-product-titles {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.mc-hero-gallery {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.mc-hero-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 1px solid var(--primary-blue);
  border-radius: 8px;
  background: white;
  cursor: pointer;
}

.mc-hero-nav-btn:hover:not(:disabled) {
  background: var(--white-hover-1);
}

.mc-hero-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mc-hero-nav-btn :deep(svg) {
  width: 10px;
  height: 18px;
}

.mc-hero-images {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 10px;
}

.mc-hero-image-wrap {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  height: 300px;
  padding: 0;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  background: transparent;
  cursor: pointer;
}

.mc-hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.mc-hero-expand {
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 24px;
  height: 24px;
  pointer-events: none;
}

.mc-activity-thumbs {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 16px;
  width: 300px;
  flex-shrink: 0;
}

.mc-activity-thumb {
  width: 142px;
  height: 142px;
  padding: 0;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  background: transparent;
  cursor: pointer;
}

.mc-activity-thumb-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mc-view-more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 142px;
  height: 142px;
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  background: none;
  cursor: pointer;
  color: var(--primary-green);
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 28px;
  letter-spacing: 0.03px;
}

.mc-view-more-text {
  text-align: left;
}

.makercrate-modal-main-content .activity-tabs {
  border-bottom: none;
}

.makercrate-modal-main-content .activity-tab {
  flex: 0 0 auto;
  padding: 16px;
  font-size: var(--fs-big);
  line-height: 28px;
  letter-spacing: 0.03px;
  border-bottom: 1px solid var(--neutral-700);
}

.makercrate-modal-main-content .activity-tab.active {
  border-bottom-width: 4px;
  border-bottom-color: var(--primary-blue);
}

.mc-activity-heading {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.makercrate-modal-main-content .current-activity-title {
  font-size: var(--fs-xxl);
  line-height: 36px;
}

.makercrate-modal-main-content .tags-container {
  margin-top: 0;
}

.makercrate-modal-main-content .content-sections {
  gap: 12px;
}

.makercrate-modal-main-content .section-content,
.makercrate-modal-main-content .formatted-outcomes {
  font-size: var(--fs-big);
  line-height: 28px;
  letter-spacing: 0.03px;
}

.makercrate-modal-main-content .sample-materials-btn {
  height: 40px;
  margin: 0 auto;
}

.mc-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.mc-details-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  height: 56px;
  padding: 4px 16px;
  border: 1px solid var(--neutral-700);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--black-body-text);
}

.mc-details-toggle:hover {
  background: #f9fafb;
}

.mc-details-toggle svg {
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.mc-details-toggle.expanded svg {
  transform: rotate(180deg);
}

.mc-details-panel {
  width: 100%;
  padding: 16px;
  border: 1px solid var(--neutral-700);
  border-radius: 8px;
}

.makercrate-modal-main-content .details-grid {
  gap: 8px;
}

.makercrate-modal-main-content .border-column {
  border-left: 1px solid var(--neutral-100);
  padding-left: 8px;
}

.makercrate-modal-main-content .detail-title {
  font-size: var(--fs-big);
  line-height: 28px;
  letter-spacing: 0.03px;
  margin: 0;
}

.makercrate-modal-main-content .detail-title-with-icon {
  align-items: flex-start;
  margin-bottom: 0;
}

.mc-info-icon {
  display: block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.mc-standards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 32px;
  max-width: 193px;
}

.makercrate-sidebar-content {
  padding: 12px;
}

.makercrate-sidebar-actions {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.makercrate-sidebar-share-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 28px;
  letter-spacing: 0.03px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: white;
  color: var(--primary-blue);
}

.makercrate-sidebar-share-btn:hover {
  background-color: #f2f3fb;
}

.makercrate-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #fce6f0;
  border-radius: 24px;
}

.makercrate-badge span {
  color: #e00069;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
}

.mobile-image-nav {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}

.mobile-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #00925e;
  background: white;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.mobile-nav-btn:not(:disabled):hover {
  background: #f0f9ff;
  transform: scale(1.05);
}

.mobile-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #d1d5db;
}

.mobile-nav-btn:disabled svg {
  color: #9ca3af;
}

.share-btn:hover {
  background-color: #f2f3fb !important;
}

@media (max-width: 1200px) {
  .mc-hero-image-wrap {
    height: 220px;
  }

  .makercrate-modal-main-content .makercart-modal-content-content {
    flex-wrap: wrap;
  }

  .mc-activity-thumbs {
    width: 100%;
  }
}

@media (max-width: 1024px) {
  .modal-main-content {
    grid-template-columns: 1fr;
    padding: 24px 16px 16px;
    overflow-x: hidden;
  }

  .makercrate-modal-main-content .makercart-sidebar {
    width: 100%;
    position: static;
  }
}

/* ========== MOBILE SPECIFIC STYLES ========== */

/* Sticky header at top of mobile modal */
.mc-mobile-sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  padding: 16px;
  background: white;
  border-bottom: 1px solid #d8dae5;
}

.mc-mobile-sticky-header .mobile-makercart-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: block;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1;
}

.mc-mobile-header-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.mc-mobile-title-block {
  padding-right: 36px;
}

.mc-mobile-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--primary-blue);
  letter-spacing: 0.024px;
  margin: 0;
  white-space: normal;
}

.mc-mobile-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: var(--primary-blue);
  letter-spacing: 0.048px;
}

.mc-mobile-price {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--primary-blue);
  letter-spacing: 0.08px;
  margin: 0;
}

/* Image with overlaid nav buttons on mobile */
.mc-mobile-image-nav {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
}

.mc-mobile-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #00925e;
  background: white;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.mc-mobile-nav-btn:not(:disabled):hover {
  background: #f0f9ff;
  transform: scale(1.05);
}

.mc-mobile-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #d1d5db;
}

.mc-mobile-nav-btn:disabled svg {
  color: #9ca3af;
}

/* Description section with light purple bg */
.mc-mobile-description-section {
  background: #ededfc;
  border-bottom: 1px solid #dfe2e7;
  padding: 16px;
  flex-shrink: 0;
  width: 100%;
}

.mc-mobile-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  letter-spacing: 0.014px;
  margin: 0;
  width: 100%;
}

/* Horizontal pill-style activity tabs */
.mc-mobile-activity-bar {
  padding: 8px 16px;
  border-bottom: 1px solid #dfe2e7;
  background: white;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  flex-shrink: 0;
  width: 100%;
}

.mc-mobile-activity-bar::-webkit-scrollbar {
  display: none;
}

.mc-mobile-activity-tabs {
  display: flex;
  gap: 8px;
  width: max-content;
}

.mc-mobile-activity-tab {
  background: white;
  border: 1px solid var(--primary-blue);
  border-radius: 8px;
  padding: 8px 12px;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  letter-spacing: 0.035px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: fit-content;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.mc-mobile-activity-tab:hover {
  background: #f9fafb;
  border-color: #00925e;
  color: #00925e;
}

.mc-mobile-activity-tab.active {
  background: var(--primary-blue);
  border-color: var(--primary-blue);
  color: white;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(0, 61, 106, 0.3);
}

.mc-mobile-activity-tab.active:hover {
  background: #002a4a;
  border-color: #002a4a;
  color: white;
}

/* Mobile section blocks (title+tags, overview, outcomes etc.) */
.mc-mobile-section {
  padding: 16px;
  border-bottom: 1px solid #dfe2e7;
  background: white;
}

.mc-mobile-section-center {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
}

.mc-mobile-section-center :deep(.tt-trigger) {
  display: block;
  width: 100%;
}

.mc-mobile-section-nopd {
  width: 100%;
}

/* Mobile footer */
.mc-mobile-footer {
  width: 100%;
  padding: 8px 16px;
  background: white;
  border-top: 1px solid #dfe2e7;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.mc-mobile-footer :deep(.action-button) {
  width: 100%;
}

.mc-footer-btn {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  transition: background-color 0.2s ease;
  border: none;
}

.mc-footer-btn-primary {
  background: var(--primary-blue);
  color: white;
}

.mc-footer-btn-primary:hover {
  background: #002a4a;
}

.mc-footer-btn-ghost {
  background: white;
  color: var(--primary-blue);
  border: none;
}

.mc-footer-btn-ghost:hover {
  background: #f2f3fb;
}

/* Make main-image-container relative so nav arrows can be positioned inside */
@media (max-width: 768px) {
  .makercrate-modal-main-content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    grid-template-columns: none;
    width: 100%;
    padding: 0;
    gap: 0;
  }

  .makercrate-modal-main-content .makercart-modal-content-container {
    gap: 0;
  }

  .makercrate-modal-main-content .makercart-modal-content-content {
    padding: 0;
    gap: 0;
    flex-wrap: wrap;
  }

  .makercrate-modal-main-content .makercart-content-section {
    gap: 0;
    padding-right: 0;
  }

  .makercrate-modal-main-content .current-activity-title {
    font-size: var(--fs-large);
    line-height: 28px;
  }

  .makercrate-modal-main-content .section-content,
  .makercrate-modal-main-content .formatted-outcomes {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .makercrate-modal-main-content .content-sections {
    flex-direction: column;
    gap: 16px;
  }

  .makercrate-modal-main-content .details-grid {
    grid-template-columns: 1fr;
  }

  .makercrate-modal-main-content .border-column {
    border-left: none;
    padding-left: 0;
  }

  .makercrate-modal-main-content
    .mc-mobile-section-center
    .sample-materials-btn {
    width: 100%;
    max-width: 100%;
    height: 40px;
    margin: 0;
    align-self: stretch;
    justify-content: center;
  }

  .mc-mobile-section-nopd .mc-details {
    padding: 16px;
  }

  .mc-standards {
    max-width: none;
    margin-top: 16px;
  }

  .main-image-container {
    position: relative;
  }

  /* Prevent horizontal scroll in the scrollable content area */
  .makercart-modal-content-container {
    overflow-x: hidden;
    max-width: 100%;
  }

  /* Remove desktop sidebar on mobile */
  .makercart-sidebar {
    display: none;
  }

  /* Full-height image on mobile */
  .makercart-gallery-section {
    width: 100% !important;
  }

  .main-image-container {
    height: 260px;
  }

  /* Sections stack vertically */
  .makercart-modal-content-content {
    flex-direction: column;
    gap: 0;
    padding: 0;
  }

  /* Desktop header hidden on mobile */
  .makercart-content-header {
    display: none;
  }

  .makercart-modal-close {
    display: none;
  }

  /* Remove desktop dividers */
  .divider {
    display: none;
  }

  /* Content section on mobile */
  .makercart-content-section {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
    width: 100%;
    overflow-y: visible;
    overflow-x: hidden;
  }

  .current-activity-title {
    font-size: var(--fs-large);
    line-height: 28px;
    margin: 0 0 8px 0;
  }

  .section-title {
    font-size: var(--fs-large);
    line-height: 28px;
    margin: 0 0 8px 0;
  }

  .section-content {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .sample-materials-btn {
    height: 40px;
    justify-content: center;
  }
}
</style>
